import {
  getApiErrorMessage,
  getApiErrorStatus,
  hasApiErrorResponse,
} from "./apiError";

export interface PortalGrantErrorFallbacks {
  network?: string;
  conflict?: string;
  forbidden?: string;
  invalid?: string;
  default?: string;
}

const DEFAULT_FALLBACKS: Required<PortalGrantErrorFallbacks> = {
  network: "Couldn't reach the server. Check your connection and try again.",
  conflict: "That action isn't allowed in the current state.",
  forbidden: "You don't have permission to perform that action.",
  invalid: "Some details are missing or invalid. Check the form and try again.",
  default: "The action couldn't be completed. Please try again.",
};

/** Fallback copy for the create-grant flow (Add Portal Access Grant forms). */
export const CREATE_GRANT_ERROR_FALLBACKS: PortalGrantErrorFallbacks = {
  conflict:
    "A portal access grant already exists for this email on this subject.",
  forbidden: "You don't have permission to grant portal access.",
  default: "Unable to create portal access grant. Please try again.",
};

/** Fallback copy for actions on an existing grant (resend/restore/revoke). */
export const EXISTING_GRANT_ERROR_FALLBACKS: PortalGrantErrorFallbacks = {
  invalid: "This grant no longer exists.",
};

/** Fallback copy for editing an existing grant's details. */
export const EDIT_GRANT_ERROR_FALLBACKS: PortalGrantErrorFallbacks = {
  conflict: "Another grant on this subject already uses that email.",
  forbidden: "You don't have permission to edit this grant.",
  invalid: "Some details are invalid. Check the form and try again.",
  default: "Unable to update portal access grant. Please try again.",
};

/**
 * Classifies a failed portal access mutation (create/restore/revoke/resend
 * grant) by HTTP status code, preferring the backend's own sanitized message
 * (see errorHandler.ts) over a guessed status-code string. `fallbacks` lets
 * each call site supply copy that fits its own action for the rare case the
 * backend response has no message at all.
 */
export function classifyPortalGrantError(
  error: unknown,
  fallbacks: PortalGrantErrorFallbacks = {}
): string {
  const merged = { ...DEFAULT_FALLBACKS, ...fallbacks };
  if (!hasApiErrorResponse(error)) {
    return merged.network;
  }

  const apiMessage = getApiErrorMessage(error);
  const status = getApiErrorStatus(error);

  if (status === 409) {
    return apiMessage || merged.conflict;
  }
  if (status === 403) {
    return apiMessage || merged.forbidden;
  }
  if (status === 400 || status === 404) {
    return apiMessage || merged.invalid;
  }
  return apiMessage || merged.default;
}
